import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { useI18n } from "@/i18n";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] overflow-hidden bg-midnight grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1543797414-a0c3ad076f7c"
          alt="Private jet window view"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/75 via-midnight/55 to-midnight/90" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative z-10 min-h-[100svh] max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-center pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="overline text-champagne mb-6"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif text-white leading-[0.94] tracking-tight text-5xl sm:text-7xl lg:text-[7.5rem] max-w-5xl">
          <MaskLine delay={0.35}>{t.hero.l1}</MaskLine>
          <MaskLine delay={0.5}><span className="italic text-gold">{t.hero.l2}</span></MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9 }}
          className="mt-8 text-cream text-lg md:text-2xl font-serif italic"
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9 }}
          className="mt-5 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
          data-testid="hero-intro"
        >
          {t.hero.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-book"
            onClick={() => scrollTo("corporate")}
            className="rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-midnight transition-transform duration-300 hover:scale-[1.04]"
          >
            {t.hero.cta}
          </button>
          <button
            data-testid="hero-cta-routes"
            onClick={() => scrollTo("how")}
            className="rounded-full border border-white/30 px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase text-white transition-colors duration-300 hover:bg-white/10"
          >
            {t.hero.cta2}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
          className="mt-16 flex items-end justify-between"
        >
          <div className="flex flex-wrap gap-6 md:gap-14">
            {t.hero.pillars.map((p) => (
              <div key={p} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                <span className="overline text-white/70">{p}</span>
              </div>
            ))}
          </div>
          <ArrowDown className="text-white/50 animate-bounce hidden sm:block" size={22} />
        </motion.div>
      </motion.div>
    </section>
  );
}
